// Event documents are uploaded files backed by a completed v2 media asset.
// Rich authored content belongs to Event Pages; a document never owns a Yjs
// room or an authored-content snapshot in the v2 model.
import { queryOptions } from "@tanstack/react-query"
import { api } from "./api"
import { v2Api, type V2ApiInit } from "./v2/client"

export type DocumentRow = {
  id: string
  eventId: string
  mediaAssetId: string
  slug: string
  title: string
  summary: string | null
  category: string
  kind: string
  yjsRoomId: null
  quickLink: number
  displayOrder: number
  attendeeVisible: boolean
  status: "draft" | "published" | "archived"
  publishedAt: string | null
  agendaItemId: string | null
  agendaActivityId: string | null
  agendaGroupId: string | null
  attendeeGroupId: string | null
  currentSnapshot: { version: number; publishedAt: string } | null
  modifiedAt?: string | null
  createdAt?: string | null
}

export type DocumentsPayload = { success: true; documents: DocumentRow[] }

export const KIND_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "generic", label: "Generic" },
  { value: "emergency_info", label: "Emergency information" },
  { value: "travel", label: "Travel info" },
  { value: "accommodation", label: "Accommodation info" },
  { value: "speaker_bio", label: "Speaker bio" },
  { value: "venue", label: "Venue info" },
  { value: "policy", label: "Policy" },
  { value: "faq", label: "FAQ" },
  { value: "sponsor", label: "Sponsor" },
  { value: "playbook", label: "Playbook" },
]
const KIND_LABEL = new Map(KIND_OPTIONS.map((k) => [k.value, k.label]))
export function kindLabel(kind: string): string {
  return KIND_LABEL.get(kind) ?? kind.replace(/[_-]/g, " ").replace(/^./, (c) => c.toUpperCase())
}

export function documentsKey(eventId: string) {
  return ["event-documents", eventId] as const
}

export function documentsQueryOptions(eventId: string) {
  return {
    queryKey: documentsKey(eventId),
    queryFn: () =>
      api<DocumentsPayload>("/api/v1/event-documents/list", {
        method: "POST",
        body: { eventId },
      }),
  }
}

export type CreateDocumentInput = {
  eventId: string
  mediaAssetId: string
  title: string
  summary?: string | null
  kind?: string
  attendeeVisible?: boolean
  quickLink?: boolean
  status?: "draft" | "published"
}

export function createEventDocument(input: CreateDocumentInput) {
  return api<{ success: true; document: DocumentRow }>("/api/v1/event-documents/create", {
    method: "POST",
    body: input,
  })
}

type MediaUploadAllocation = {
  success: true
  mediaId: string
  key: string
  uploadUrl: string
  method: string
  headers: Record<string, string>
}

type MediaUploadRequest = <T>(path: string, init: V2ApiInit) => Promise<T>
type ObjectUpload = (
  url: string,
  init: RequestInit,
) => Promise<Pick<Response, "ok" | "status">>

export type DocumentUploadDependencies = {
  request?: MediaUploadRequest
  upload?: ObjectUpload
}

/** Allocate, PUT, and complete one v2 media asset for an event document. */
export async function uploadCompletedEventMedia(
  eventId: string,
  file: File,
  dependencies: DocumentUploadDependencies = {},
): Promise<string> {
  const request = dependencies.request ?? v2Api
  const upload = dependencies.upload ?? fetch
  const allocation = await request<MediaUploadAllocation>(
    `/api/v2/events/${encodeURIComponent(eventId)}/media/uploads`,
    {
      method: "POST",
      body: {
        idempotencyKey: crypto.randomUUID(),
        filename: file.name || "document",
        mimeType: file.type || "application/octet-stream",
        byteSize: file.size,
      },
    },
  )

  if (!allocation.mediaId || !allocation.uploadUrl) {
    throw new Error("Attend v2 did not allocate a document upload")
  }

  const uploaded = await upload(allocation.uploadUrl, {
    method: allocation.method || "PUT",
    headers: allocation.headers,
    body: file,
  })
  if (!uploaded.ok) throw new Error(`Document upload failed (${uploaded.status})`)

  await request(
    `/api/v2/events/${encodeURIComponent(eventId)}/media/${encodeURIComponent(allocation.mediaId)}/complete`,
    {
      method: "POST",
      body: { idempotencyKey: crypto.randomUUID() },
    },
  )
  return allocation.mediaId
}

export const uploadEventDocumentFile = uploadCompletedEventMedia

export type DocumentMediaResult = {
  success: true
  url: string | null
  expiresAt: string | null
  media: {
    id: string
    originalFilename: string
    mimeType: string
    byteSize: number
    status: string
  }
}

export function documentMediaQueryOptions(
  eventId: string,
  mediaAssetId: string,
) {
  return queryOptions({
    queryKey: ["event-document-media", eventId, mediaAssetId] as const,
    queryFn: () =>
      v2Api<DocumentMediaResult>(
        `/api/v2/events/${encodeURIComponent(eventId)}/media/${encodeURIComponent(mediaAssetId)}`,
      ),
    staleTime: 8 * 60 * 1000,
  })
}

export type DocumentPatch = {
  title?: string
  kind?: string
  quickLink?: boolean
}

export function updateEventDocument(documentId: string, patch: DocumentPatch) {
  return api<{ success: true }>("/api/v1/event-documents/update", {
    method: "POST",
    body: { documentId, ...patch },
  })
}

export function deleteEventDocument(documentId: string) {
  return api<{ success: true; documentId: string }>("/api/v1/event-documents/delete", {
    method: "POST",
    body: { documentId },
  })
}

export type PublishDocumentResult = { success: true; version: number; htmlR2Key: string | null }

export function publishEventDocument(documentId: string) {
  return api<PublishDocumentResult>("/api/v1/event-documents/publish", {
    method: "POST",
    body: { documentId },
  })
}
